/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn, IDetailsList, IDetailsRowProps, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { IRenderFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { QnaMakerAnswerColumnRenderer } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerAnswerColumnRenderer';
import { QnaMakerContextColumnRenderer } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerContextColumnRenderer';
import { QnaData, QnaMakerQnaRowRenderer } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerQnaRowRenderer';
import { QnaMakerQuestionsColumnRenderer } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerQuestionsColumnRenderer';
import { getFlattenMetadata } from 'src/qnaMaker/appShell/qnaMakerEditPage/qnaMakerFiltersUtils';
import { QnaMakerQnaPairsBeingEditedStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerQnaPairsBeingEditedStore';
import { OptionalColumnVisibility } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerQnaPairsUiStore';
import { QnaMakerMetadata } from 'src/qnaMaker/core/components/QnaMakerMetadata';
import { QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerQnaStore } from 'src/qnaMaker/core/stores/QnaMakerQnaStore';
import { QnaMakerSourceStore } from 'src/qnaMaker/core/stores/QnaMakerSourceStore';
import { QnaMakerContextTreeNode } from 'src/qnaMaker/core/types/QnaMakerContextTreeNode';

export type QnaMakerDetailsTableProps = TableProps<QnaMakerQnaStore> & {
    isRichTextEnabled: boolean;
    openedPromptModalId: number;
    isContextTreeCalculating: boolean;
    qnaPairsJson: QnaMakerQnaSwagger[];
    sourceStores: QnaMakerSourceStore[];
    areQnaTableActionsDisabled: boolean;
    onDismissEditQnaPair: (id: number) => void;
    onOpenedPromptModalChange: (value: number) => void;
    optionalColumnsVisibility: OptionalColumnVisibility[];
    handleQnaContextualNavigation: (qnaId: number) => void;
    contextTreeMap: Map<number, QnaMakerContextTreeNode[]>;
    qnaPairsBeingEditedStore: QnaMakerQnaPairsBeingEditedStore;
    onSubmitEditQnaPair: (qnaId: number, newData: Partial<QnaMakerQnaSwagger>) => void;
};

const messages = defineMessages({
    context: {
        id: 'qnaMaker.qnaMakerDetailsTable.context',
        defaultMessage: 'Context'
    },
    questions: {
        id: 'qnaMaker.qnaMakerDetailsTable.questions',
        defaultMessage: 'Questions'
    },
    answerAndPrompts: {
        id: 'qnaMaker.qnaMakerDetailsTable.answerAndPrompts',
        defaultMessage: 'Answer and prompts'
    },
    metadata: {
        id: 'qnaMaker.qnaMakerDetailsTable.metadata',
        defaultMessage: 'Metadata'
    },
    source: {
        id: 'qnaMaker.qnaMakerDetailsTable.source',
        defaultMessage: 'Source'
    },
    submit: {
        id: 'qnaMaker.qnaMakerDetailsTable.submit',
        defaultMessage: 'Submit'
    },
    cancel: {
        id: 'qnaMaker.qnaMakerDetailsTable.cancel',
        defaultMessage: 'Cancel'
    }
});

const { getClassName } = getStylistV2('QnaMakerDetailsTable');

const tableCheckboxClassName = getClassName('tableCheckboxClassName', {
    top: 2
});

const tableTheme = getScopedTheme('Table');

/**
 * A component displaying question and answer pairs along with their details.
 */
export const QnaMakerDetailsTable = observer((props: QnaMakerDetailsTableProps) => {
    const {
        qnaPairsJson,
        sourceStores,
        onColumnClick,
        contextTreeMap,
        isRichTextEnabled,
        onSubmitEditQnaPair,
        openedPromptModalId,
        onDismissEditQnaPair,
        onOpenedPromptModalChange,
        isContextTreeCalculating,
        qnaPairsBeingEditedStore,
        optionalColumnsVisibility,
        areQnaTableActionsDisabled,
        handleQnaContextualNavigation
    } = props;

    const { localize } = useLocalization();

    const tableRef = React.createRef<IDetailsList>();

    const onDismissEditPair = (idx: number, id: number) => {
        tableRef.current?.focusIndex(idx);
        onDismissEditQnaPair(id);
    };

    const onSubmitEditPair = (idx: number, newData: Partial<QnaData>) => {
        tableRef.current?.focusIndex(idx);
        if (newData.linkedPair) {
            // Linked pair modified from the prompts
            onSubmitEditQnaPair(newData.linkedPair.id, { ...newData.linkedPair });
        }
        onSubmitEditQnaPair(newData.id, { ...newData });
    };

    const onRenderContext = (item: QnaMakerQnaStore) => (
        <QnaMakerContextColumnRenderer
            qnaId={item.id}
            isContextTreeCalculating={isContextTreeCalculating}
            isContextOnly={item.dialog.isContextOnly}
            contextTrees={contextTreeMap.get(item.id) || []}
            onClickQna={handleQnaContextualNavigation}
        />
    );

    const onRenderQuestions = (item: QnaMakerQnaStore) => (
        <QnaMakerQuestionsColumnRenderer
            pairId={item.id}
            questions={item.questions}
            onEditQuestions={(questions, activeLearningSuggestions) =>
                onSubmitEditQnaPair(item.id, { questions, activeLearningSuggestions })
            }
            isAddActionDisabled={areQnaTableActionsDisabled}
        />
    );

    const onRenderAnswerAndPrompts = (item: QnaMakerQnaStore) => (
        <QnaMakerAnswerColumnRenderer
            pairId={item.id}
            answer={item.answer}
            selectedQnaId={item.id}
            qnaPairsJson={qnaPairsJson}
            prompts={item.dialog.prompts}
            isRichTextEnabled={isRichTextEnabled}
            isPromptModalHidden={openedPromptModalId !== item.id}
            setIsPromptModalHidden={val => onOpenedPromptModalChange(val ? null : item.id)}
            isContextOnly={item.dialog.isContextOnly}
            isAddActionDisabled={areQnaTableActionsDisabled}
            onChange={(answer, prompts, modifiedPair) => {
                // existing pairs needs to modify its questions or context flag
                if (modifiedPair) {
                    onSubmitEditQnaPair(modifiedPair.id, modifiedPair);
                }

                onSubmitEditQnaPair(item.id, {
                    answer,
                    dialog: { prompts: prompts.map((p, i) => ({ ...p, displayOrder: i })), isContextOnly: item.dialog.isContextOnly }
                });
            }}
        />
    );

    const onRenderMetadata = (item: QnaMakerQnaStore) => (
        <QnaMakerMetadata
            pairId={item.id}
            metadata={item.metadata}
            isAddActionDisabled={areQnaTableActionsDisabled}
            flattenMetadata={getFlattenMetadata(qnaPairsJson)}
            onChangeMetadata={metadata => onSubmitEditQnaPair(item.id, { metadata })}
        />
    );

    const onRenderRow: IRenderFunction<IDetailsRowProps> = rowProps => (
        <QnaMakerQnaRowRenderer
            rowProps={rowProps}
            qnaPairsJson={qnaPairsJson}
            sourceStores={sourceStores}
            isRichTextEnabled={isRichTextEnabled}
            onDismissChanges={id => onDismissEditPair(rowProps.itemIndex, id)}
            openedPromptModalId={openedPromptModalId}
            onOpenedPromptModalChange={onOpenedPromptModalChange}
            isAddActionDisabled={areQnaTableActionsDisabled}
            qnaPairsBeingEditedStore={qnaPairsBeingEditedStore}
            onSubmitChanges={newData => onSubmitEditPair(rowProps.itemIndex, newData)}
            isExpandView
        />
    );

    const columns: IColumn[] = [
        {
            key: 'questions',
            onColumnClick,
            minWidth: 300,
            maxWidth: 400,
            isRowHeader: false,
            fieldName: 'questions',
            isResizable: true,
            isCollapsible: false,
            name: localize(messages.questions),
            onRender: onRenderQuestions,
            styles: { cellName: { color: tableTheme.headerCellNameColor, fontWeight: tableTheme.headerCellNameFontWeight } }
        },
        {
            key: 'answerAndPrompts',
            onColumnClick,
            minWidth: 300,
            maxWidth: 400,
            isRowHeader: false,
            fieldName: 'answerAndPrompts',
            isResizable: true,
            isCollapsible: false,
            name: localize(messages.answerAndPrompts),
            onRender: onRenderAnswerAndPrompts,
            styles: { cellName: { color: tableTheme.headerCellNameColor, fontWeight: tableTheme.headerCellNameFontWeight } }
        }
    ];

    if (optionalColumnsVisibility.find(c => c.key === 'context').isVisible) {
        columns.unshift({
            key: 'context',
            onColumnClick,
            minWidth: 200,
            maxWidth: 700,
            isRowHeader: true,
            fieldName: 'context',
            isResizable: true,
            isCollapsible: false,
            name: localize(messages.context),
            onRender: onRenderContext,
            styles: { cellName: { color: tableTheme.headerCellNameColor, fontWeight: tableTheme.headerCellNameFontWeight } }
        });
    }

    if (optionalColumnsVisibility.find(c => c.key === 'metadata').isVisible) {
        columns.push({
            key: 'metadata',
            onColumnClick,
            minWidth: 200,
            maxWidth: 300,
            isRowHeader: false,
            fieldName: 'metadata',
            isResizable: true,
            isCollapsible: false,
            name: localize(messages.metadata),
            onRender: onRenderMetadata,
            styles: { cellName: { color: tableTheme.headerCellNameColor, fontWeight: tableTheme.headerCellNameFontWeight } }
        });
    }

    return (
        <Table
            columns={columns}
            onRenderRow={onRenderRow}
            selectionMode={SelectionMode.none}
            isHeaderVisible={false}
            checkboxCellClassName={tableCheckboxClassName}
            {...props}
            componentRef={tableRef}
        />
    );
});
